import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getVoiceKey } from "@/lib/golden";
import { ArrowLeft } from "lucide-react";
import { Brand } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CopyBlock } from "@/components/CopyBlock";
import { ALEXA_MODEL } from "@/lib/alexaModel";
import { API } from "@/lib/api";
import { Nice, niceText } from "@/components/Nice";

const SIRI_STEPS = [
  "On your child's iPhone or iPad, open the Shortcuts app and tap + to make a new shortcut. Name it NICE.",
  "Add a “Text” action and type a family nickname, like siri-maya. This keeps each child's conversation separate.",
  "Add “Repeat” and set it to 6 times. Put everything below inside the repeat.",
  "Add “Dictate Text” (Stop listening: After Pause).",
  "Add “Get Contents of URL” with the URL below. Tap Show More: Method POST, Request Body JSON with the four fields below. Your private family_key lets Siri Golden Acorns appear in your Golden Acorn Collection.",
  "Add “Get Dictionary Value” for the key speak, then add “Speak Text” with that value.",
  "Add “Get Dictionary Value” for the key done. Then add “If Dictionary Value is 1 (true)” → “Stop This Shortcut”.",
  "Done! Your child can now say “Hey Siri, NICE”, ask with please, and share some words of gratitude to hear the answer.",
];

const ALEXA_STEPS = [
  "Sign in at developer.amazon.com/alexa/console/ask with a free Amazon developer account and click Create Skill.",
  "Name it NICE Manners, choose Custom model and “Provision your own” backend, then start from scratch.",
  "Open Interaction Model → JSON Editor, paste the model below, then Save and Build.",
  "Open Endpoint, choose HTTPS, paste the endpoint URL below, and choose “My development endpoint is a sub-domain of a domain that has a wildcard certificate”.",
  "Open the Test tab, enable Development, and say or type “open nice manners”. It also works on any Echo signed in to the same Amazon account.",
  "Ready for the public store? Every request is already checked against Amazon's signature and timestamp rules. Copy your Skill ID (starts with amzn1.ask.skill) so NICE only answers your skill, then submit it under Certification.",
];

const Steps = ({ items, testId }) => (
  <ol className="pp-steps" data-testid={testId}>
    {items.map((s, i) => <li key={s}><span>{i + 1}</span><p>{niceText(s)}</p></li>)}
  </ol>
);

export default function VoiceSetup() {
  const [key, setKey] = useState("");
  useEffect(() => { getVoiceKey().then(setKey).catch(() => {}); }, []);
  const body = JSON.stringify({ text: "Dictated Text", session_id: "Text", age: "kid", family_key: key || "loading…" }, null, 2);
  return (
    <div className="pp-app mode-kid" data-testid="voice-setup-page">
      <header className="pp-header">
        <Brand />
        <Link to="/parents" className="pp-ghost" data-testid="back-to-tips-link"><ArrowLeft className="h-4 w-4" /> Parent tips</Link>
      </header>
      <main className="pp-main">
        <p className="pp-label">For parents · voice assistants</p>
        <h1 className="pp-h1"><Nice /> on Siri & Alexa</h1>
        <p className="pp-sub max-w-xl">Same rule everywhere: no please, no answer. A thank-you shares the answer, and thoughtful thanks earns a Golden Acorn.</p>

        <section className="pp-card mt-10" data-testid="siri-setup-card">
          <h2 className="pp-h2">“Hey Siri, <Nice />” — Apple Shortcut</h2>
          <Steps items={SIRI_STEPS} testId="siri-steps" />
          <p className="pp-label mt-6">URL</p>
          <CopyBlock value={`${API}/voice/turn`} testId="siri-url" />
          <p className="pp-label mt-4">Request body (JSON fields)</p>
          <CopyBlock value={body} testId="siri-body" pre />
          <p className="pp-sub text-sm">Use the Dictated Text and Text variables for the first two fields. Keep your family_key private, like a password. Already set up Siri? Just add the family_key field.</p>
        </section>

        <section className="pp-card mt-8" data-testid="alexa-setup-card">
          <h2 className="pp-h2">“Alexa, open Nice Manners” — Alexa Skill</h2>
          <Steps items={ALEXA_STEPS} testId="alexa-steps" />
          <p className="pp-label mt-6">Endpoint URL</p>
          <CopyBlock value={`${API}/alexa`} testId="alexa-endpoint" />
          <p className="pp-label mt-4">Interaction model</p>
          <CopyBlock value={JSON.stringify(ALEXA_MODEL, null, 2)} testId="alexa-model" pre />
        </section>
      </main>
      <Footer />
    </div>
  );
}
